package com.partejar.app.core.ui.components

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.layout
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme

enum class PartejarDockDestination(
    val label: String
) {
    HOME("Home"),
    GESTANTES("Gestantes"),
    RELACIONAMENTOS("Relacionamentos"),
    AGENDA("Agenda"),
    CONFIGURACOES("Configurações")
}

@Composable
fun PartejarFloatingDock(
    selectedDestination: PartejarDockDestination,
    onDestinationSelected: (PartejarDockDestination) -> Unit,
    desktop: Boolean = false,
    modifier: Modifier = Modifier
) {
    val dimensions = PartejarTheme.dimensions
    val spacing = PartejarTheme.spacing
    val destinations = PartejarDockDestination.entries
    val selectedIndex = destinations.indexOf(selectedDestination)

    if (desktop) {
        Surface(
            modifier = modifier
                .widthIn(max = PartejarLayout.dockMaxWidth)
                .fillMaxWidth()
                .heightIn(min = PartejarLayout.desktopDockHeight),
            shape = PartejarShapes.pill,
            color = MaterialTheme.colorScheme.surfaceContainerLow.copy(alpha = 0.94f),
            contentColor = MaterialTheme.colorScheme.onSurface,
            border = BorderStroke(
                dimensions.strokeThin,
                MaterialTheme.colorScheme.outline.copy(alpha = 0.58f)
            ),
            shadowElevation = spacing.space4
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = spacing.space8, vertical = spacing.space4)
                    .selectableGroup(),
                horizontalArrangement = Arrangement.spacedBy(spacing.space4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                destinations.forEach { destination ->
                    val selected = destination == selectedDestination
                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .heightIn(min = PartejarLayout.desktopDockHeight - spacing.space8)
                            .clip(PartejarShapes.pill)
                            .selectable(
                                selected = selected,
                                onClick = { onDestinationSelected(destination) },
                                role = Role.Tab
                            )
                            .semantics { contentDescription = destination.label }
                            .padding(vertical = spacing.space4),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(spacing.space4)
                    ) {
                        Box(
                            modifier = Modifier.size(dimensions.minimumTouchTarget - spacing.space8),
                            contentAlignment = Alignment.Center
                        ) {
                            if (selected) {
                                Surface(
                                    modifier = Modifier.size(dimensions.minimumTouchTarget - spacing.space8),
                                    shape = PartejarShapes.radius20,
                                    color = MaterialTheme.colorScheme.primaryContainer,
                                    content = {}
                                )
                            }
                            Icon(
                                imageVector = destination.icon(),
                                contentDescription = null,
                                modifier = Modifier.size(dimensions.iconMedium),
                                tint = if (selected) {
                                    MaterialTheme.colorScheme.primary
                                } else {
                                    MaterialTheme.colorScheme.onSurfaceVariant
                                }
                            )
                        }
                        Text(
                            text = destination.label,
                            style = MaterialTheme.typography.labelSmall,
                            color = if (selected) {
                                MaterialTheme.colorScheme.primary
                            } else {
                                MaterialTheme.colorScheme.onSurfaceVariant
                            },
                            modifier = Modifier.fillMaxWidth(),
                            maxLines = 1,
                            softWrap = false,
                            overflow = TextOverflow.Ellipsis,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }
    } else {
        Column(
            modifier = modifier.fillMaxWidth()
        ) {
        BoxWithConstraints(
            modifier = Modifier.fillMaxWidth()
        ) {
            val itemWidth = maxWidth / destinations.size
            val targetCenter = (itemWidth * selectedIndex) + (itemWidth / 2)

            val animatedCenter by animateDpAsState(
                targetValue = targetCenter,
                animationSpec = tween(durationMillis = 240),
                label = "dockLabelOffset"
            )

            Box(
                modifier = Modifier.layout { measurable, constraints ->
                    val placeable = measurable.measure(constraints.copy(minWidth = 0))

                    val centerPx = animatedCenter.roundToPx()
                    val startX = centerPx - (placeable.width / 2)
                    val clampedX = startX.coerceIn(0, constraints.maxWidth - placeable.width)

                    layout(constraints.maxWidth, placeable.height) {
                        placeable.placeRelative(x = clampedX, y = 0)
                    }
                }
            ) {
                Surface(
                    shape = PartejarShapes.pill,
                    color = MaterialTheme.colorScheme.surfaceContainerLow,
                    contentColor = MaterialTheme.colorScheme.primary,
                    border = BorderStroke(
                        dimensions.strokeThin,
                        MaterialTheme.colorScheme.outline.copy(alpha = 0.58f)
                    ),
                    shadowElevation = dimensions.floatingDockElevation
                ) {
                    AnimatedContent(
                        targetState = selectedDestination,
                        transitionSpec = {
                            fadeIn(animationSpec = tween(160)) togetherWith fadeOut(animationSpec = tween(120))
                        },
                        label = "dockLabelContent"
                    ) { destination ->
                        Text(
                            text = destination.label,
                            modifier = Modifier.padding(
                                horizontal = spacing.space16,
                                vertical = spacing.space8
                            ),
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.primary
                        )
                    }
                }
            }
        }

        Box(
            modifier = Modifier.padding(top = spacing.space8)
        ) {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = PartejarShapes.radius28,
                color = MaterialTheme.colorScheme.surfaceContainerLow,
                contentColor = MaterialTheme.colorScheme.onSurface,
                border = BorderStroke(
                    dimensions.strokeThin,
                    MaterialTheme.colorScheme.outline.copy(alpha = 0.58f)
                ),
                shadowElevation = dimensions.floatingDockElevation
            ) {
                BoxWithConstraints(
                    modifier = Modifier
                        .heightIn(min = dimensions.floatingDockMinHeight)
                        .padding(spacing.space8)
                ) {
                    val itemWidth = maxWidth / destinations.size
                    val targetIndicatorOffset = itemWidth * selectedIndex

                    val indicatorOffset by animateDpAsState(
                        targetValue = targetIndicatorOffset,
                        animationSpec = tween(durationMillis = 240),
                        label = "dockIndicatorOffset"
                    )

                    Surface(
                        modifier = Modifier
                            .width(itemWidth)
                            .heightIn(min = dimensions.minimumTouchTarget)
                            .offset(x = indicatorOffset),
                        shape = PartejarShapes.pill,
                        color = MaterialTheme.colorScheme.primaryContainer
                    ) {}

                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .selectableGroup(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        destinations.forEach { destination ->
                            val selected = destination == selectedDestination
                            Box(
                                modifier = Modifier
                                    .width(itemWidth)
                                    .heightIn(min = dimensions.minimumTouchTarget)
                                    .clip(PartejarShapes.pill)
                                    .selectable(
                                        selected = selected,
                                        onClick = { onDestinationSelected(destination) },
                                        role = Role.Tab
                                    )
                                    .semantics { contentDescription = destination.label },
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    imageVector = destination.icon(),
                                    contentDescription = null,
                                    modifier = Modifier.size(dimensions.iconDefault),
                                    tint = if (selected) {
                                        MaterialTheme.colorScheme.primary
                                    } else {
                                        MaterialTheme.colorScheme.onSurfaceVariant
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
    }
}

private fun PartejarDockDestination.icon(): ImageVector = when (this) {
    PartejarDockDestination.HOME -> PartejarIcons.Navigation.home
    PartejarDockDestination.GESTANTES -> PartejarIcons.Navigation.gestantes
    PartejarDockDestination.RELACIONAMENTOS -> PartejarIcons.Navigation.relacionamentos
    PartejarDockDestination.AGENDA -> PartejarIcons.Navigation.agenda
    PartejarDockDestination.CONFIGURACOES -> PartejarIcons.Navigation.settings
}
